import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { AlertTriangle, Package, User, CheckCircle, Pencil, HelpCircle } from 'lucide-react'
import { defekte as defekteApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { defektZustandBadge, defektStatusBadge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Select } from '../../components/ui/Select'
import { Textarea } from '../../components/ui/Textarea'
import { LoadingSpinner, ErrorMessage } from '../../components/ui/EmptyState'
import { formatDatumZeit } from '../../lib/utils'
import type { Defekt } from '../../types'

export function DefektDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['defekt', id],
    queryFn: () => defekteApi.get(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (d: Partial<Defekt>) => defekteApi.update(id!, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['defekt', id] }); qc.invalidateQueries({ queryKey: ['defekte'] }); setEditOpen(false) },
  })

  if (isLoading) return <><TopBar title="Defekt" backTo="/defekte" /><LoadingSpinner /></>
  if (error || !data) return <><TopBar title="Fehler" backTo="/defekte" /><ErrorMessage message="Defekt nicht gefunden" /></>

  return (
    <>
      <TopBar
        title="Defekt"
        subtitle={data.kunde_name}
        backTo="/defekte"
        rightAction={<Button variant="outline" size="sm" icon={<Pencil size={14} />} onClick={() => setEditOpen(true)}>Bearbeiten</Button>}
      />
      <div className="px-4 py-3 space-y-4 pb-8">
        {/* Status */}
        <div className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <AlertTriangle size={20} className="text-red-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900">{data.beschreibung}</p>
            <p className="text-xs text-gray-500">{formatDatumZeit(data.erstellt_am)}</p>
          </div>
        </div>

        {/* Badges */}
        <div className="flex gap-2">
          {defektZustandBadge(data.zustand)}
          {defektStatusBadge(data.status)}
        </div>

        {/* Details */}
        <Card>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Kunde</p>
              <button className="font-medium text-blue-600" onClick={() => navigate(`/kunden/${data.kunde_id}`)}>{data.kunde_name}</button>
            </div>
            {data.geraet_modell && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Gerät</p>
                <p className="font-medium flex items-center gap-2">
                  <Package size={14} className="text-gray-400" />
                  {data.geraet_modell} {data.seriennummer ? `(${data.seriennummer})` : ''}
                </p>
              </div>
            )}
            {data.ursache && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Ursache</p>
                <p className="font-medium">{data.ursache}</p>
              </div>
            )}
            {data.massnahmen && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Massnahmen</p>
                <p className="text-gray-800 whitespace-pre-wrap">{data.massnahmen}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Actions */}
        {data.status !== 'behoben' && (
          <Button
            fullWidth
            icon={<CheckCircle size={16} />}
            onClick={() => updateMutation.mutate({ ...data, status: 'behoben' })}
            loading={updateMutation.isPending}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            Als behoben markieren
          </Button>
        )}

        <Button
          variant="secondary"
          fullWidth
          icon={<HelpCircle size={16} />}
          onClick={() => navigate(`/faq?modell=${data.geraet_modell ?? 'alle'}`)}
        >
          FAQ & Selbsthilfe anzeigen
        </Button>
      </div>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Defekt bearbeiten"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setEditOpen(false)}>Abbrechen</Button>
            <Button fullWidth loading={updateMutation.isPending} onClick={() => document.getElementById('defekt-edit-submit')?.click()}>Speichern</Button>
          </div>
        }
      >
        <EditDefektForm data={data} onSubmit={(d) => updateMutation.mutate(d)} />
      </Modal>
    </>
  )
}

function EditDefektForm({ data, onSubmit }: { data: Defekt; onSubmit: (d: Partial<Defekt>) => void }) {
  const [form, setForm] = useState({ ...data })
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form) }} className="space-y-4">
      <Textarea label="Beschreibung" value={form.beschreibung} onChange={(e) => set('beschreibung', e.target.value)} rows={3} required />
      <Select label="Zustand" value={form.zustand} onChange={(e) => set('zustand', e.target.value)}
        options={[
          { value: 'leicht', label: 'Leicht' },
          { value: 'mittel', label: 'Mittel' },
          { value: 'schwer', label: 'Schwer' },
          { value: 'total-ausfall', label: 'Totalausfall' },
        ]}
      />
      <Select label="Status" value={form.status} onChange={(e) => set('status', e.target.value)}
        options={[
          { value: 'offen', label: 'Offen' },
          { value: 'in-bearbeitung', label: 'In Bearbeitung' },
          { value: 'behoben', label: 'Behoben' },
        ]}
      />
      <Select label="Ursache" value={form.ursache ?? ''} onChange={(e) => set('ursache', e.target.value)}
        options={[
          { value: '', label: '–' },
          { value: 'Benutzerfehler', label: 'Benutzerfehler' },
          { value: 'Verschleiss', label: 'Verschleiss' },
          { value: 'Fabrikfehler', label: 'Fabrikfehler' },
          { value: 'Unfall', label: 'Unfall' },
          { value: 'Kalk', label: 'Kalkablagerungen' },
          { value: 'Elektrisch', label: 'Elektrischer Defekt' },
          { value: 'Unbekannt', label: 'Unbekannt' },
        ]}
      />
      <Textarea label="Massnahmen" value={form.massnahmen ?? ''} onChange={(e) => set('massnahmen', e.target.value)} rows={3} />
      <button id="defekt-edit-submit" type="submit" className="hidden" />
    </form>
  )
}
